import React, { useRef, useState } from "react";
import CodeEditor, { CodeEditorRef } from "@/interview-engine/codeeditor/CodeEditor";

type SupportedLanguage = "javascript" | "python" | "java" | "c" | "cpp";

// Dummy placeholder code for each language
// Note: The compiler automatically adds the function call for test cases
const placeholderCode: Record<SupportedLanguage, string> = {
  javascript: `// Write a function that adds two numbers
function add(a, b) {
  // Your code here
  return a + b;
}`,
  python: `# Write a function that adds two numbers
def add(a, b):
    # Your code here
    return a + b`,
  java: `// Write a function that adds two numbers
public class Main {
    public static int add(int a, int b) {
        // Your code here
        return a + b;
    }
    
    public static void main(String[] args) {
        System.out.println(add(2, 3));
    }
}`,
  c: `// Write a function that adds two numbers
#include <stdio.h>

int add(int a, int b) {
    // Your code here
    return a + b;
}

int main() {
    printf("%d\\n", add(2, 3));
    return 0;
}`,
  cpp: `// Write a function that adds two numbers
#include <iostream>
using namespace std;

int add(int a, int b) {
    // Your code here
    return a + b;
}

int main() {
    cout << add(2, 3) << endl;
    return 0;
}`,
};

// Dummy test cases for the add function
const testCases = [
  {
    input: "2, 3",
    expectedOutput: "5",
    description: "Add 2 and 3",
    fullExecutableCode: "",
  },
  {
    input: "10, 20",
    expectedOutput: "30",
    description: "Add 10 and 20",
    fullExecutableCode: "",
  },
  {
    input: "-5, 5",
    expectedOutput: "0",
    description: "Add -5 and 5",
    fullExecutableCode: "",
  },
];

export default function CodeEditorExample() {
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage>("javascript");
  const [submittedCode, setSubmittedCode] = useState<string>("");
  const codeEditorRef = useRef<CodeEditorRef>(null);

  const handleSubmit = (code: string) => {
    console.log("Code submitted:", code);
    setSubmittedCode(code);
  };

  const handleDsaQuestionSubmit = (response: any) => {
    console.log("DSA Question submitted:", response);
  };

  const handleUserSubmit = () => {
    console.log("User clicked submit");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Code Editor Example</h1>
          <p className="text-slate-400">
            Test the code editor component with various programming languages.
            Write a function that adds two numbers and test it against the provided test cases.
          </p>
        </div>

        {/* Code Editor Container */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden shadow-2xl">
          <div className="h-[600px]">
            <CodeEditor
              ref={codeEditorRef}
              placeholder={placeholderCode[selectedLanguage]}
              language={selectedLanguage}
              selectedLanguage={selectedLanguage}
              setSelectedLanguage={setSelectedLanguage}
              onSubmit={handleSubmit}
              testCases={testCases}
              handleDsaQuestionSubmit={handleDsaQuestionSubmit}
              onUserSubmit={handleUserSubmit}
            />
          </div>
        </div>

        {/* Info Panel */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Test Cases Info */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Test Cases</h2>
            <div className="space-y-3">
              {testCases.map((tc, idx) => (
                <div key={idx} className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                  <div className="text-sm text-slate-400 mb-1">{tc.description}</div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-slate-300">
                      Input: <code className="text-emerald-400">add({tc.input})</code>
                    </span>
                    <span className="text-slate-300">
                      Expected: <code className="text-emerald-400">{tc.expectedOutput}</code>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Instructions</h2>
            <div className="space-y-4 text-slate-300 text-sm">
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-xs font-bold">1</span>
                <p>Write your code in the editor above. You can switch between JavaScript, Python, Java, C, and C++.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-xs font-bold">2</span>
                <p>Click <strong className="text-emerald-400">Run</strong> to execute your code and see the output in the console.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-xs font-bold">3</span>
                <p>Click <strong className="text-emerald-400">Test</strong> to run all test cases and see the results.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-xs font-bold">4</span>
                <p>Once all tests pass, click <strong className="text-emerald-400">Submit</strong> to submit your solution.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Submitted Code Display */}
        {submittedCode && (
          <div className="mt-8 bg-slate-800/50 rounded-xl border border-slate-700 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Submitted Code</h2>
            <pre className="bg-slate-900 rounded-lg p-4 text-sm text-slate-300 overflow-x-auto">
              <code>{submittedCode}</code>
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

